import { useEffect, useMemo, useState } from "react";
import { SystemLanguage } from "../../common/system-language";
import {
  DialectDto,
  DialectVoiceDto,
  LanguageDto,
  getDialects,
  getEnabledLanguages,
  getVoicesForDialect,
} from "../../api/languageService";
import { extractBaseLanguage } from "../../common/helpers";
import { ARABIC_DIALECTS } from "../../common/dialects";
// Auto Detect is commented out, so the mobile pin that depended on it is unused.
// import { isMobileDevice } from "../../common/device";
import "./PersonalizationModal.css";

interface AvatarOption {
  id: number | string;
  name: string;
  thumbnail: string;
  glbPath?: string;
  elevenLabsVoiceId: string;
  audioDemoPath: string;
  gender: number;
  description: string;
  isDefault: boolean;
  bgColor: string | null;
  themeId: number;
  personalityTags: string[];
  languageCodes: string[];
  languageNames: string[];
  avatarType?: "GLB" | "IMAGE";
  imagePath?: string;
  avtrAvatarId?: string;
  avtrBackgroundId?: string;
  inferenceRegistrationStatus?: string;
}

type PersonalizationValue = {
  speakerLanguage: string;
  avatarLanguage: string;
  /** Empty string when the user hasn't overridden; resolves to avatar.elevenLabsVoiceId at runtime. */
  avatarVoiceId: string;
  selectedAvatarId: string;
  systemLanguage: string;
  tapToTalk: boolean;
  floatingChatEnabled: boolean;
};

interface Props {
  open: boolean;
  onClose: () => void;
  value: PersonalizationValue;
  onSave: (val: PersonalizationValue) => void;
  avatarOptions: AvatarOption[];
  systemLanguage: SystemLanguage;
}

const SPEAKER_AUTO = "auto";

type DialectOption = { value: string; label: string };

/**
 * Dialect presentation for the settings dropdowns. ace-common stores dialects as
 * raw iso codes ("ar-LB", "en-US"); the UI shows friendly names instead.
 *
 * The table lives in common/dialects.ts because the chat request needs it too: what is
 * picked here is what the avatar is asked to speak. Every language other than Arabic
 * exposes a single "Default" entry, whose value is empty so the stored language stays
 * the plain base iso ("en", "fr", "de").
 */

export default function PersonalizationModal({
  open,
  onClose,
  value,
  onSave,
  avatarOptions,
  systemLanguage,
}: Props) {
  const [activeTab, setActiveTab] = useState<"general" | "avatars" | "preferences" | "info">("general");
  const [draft, setDraft] = useState<PersonalizationValue>(value);
  // Local thumbnails (synthetic AVTR avatars) whose image file is missing — show an initial instead.
  const [enabledLanguages, setEnabledLanguages] = useState<LanguageDto[]>([]);
  const [dialectsByLang, setDialectsByLang] = useState<Record<string, DialectDto[]>>({});
  const [voicesByDialect, setVoicesByDialect] = useState<Record<string, DialectVoiceDto[]>>({});

  const isArabicUi = systemLanguage === "ar";
  const onChange = (next: PersonalizationValue) => setDraft(next);

  /**
   * Phones and tablets are pinned to Auto Detect: it's the only speaker setting whose STT engine
   * (the Whisper socket) works reliably on mobile browsers. Picking a fixed language there routes
   * transcription through the Web Speech API instead, which mobile engines support unevenly. The
   * dropdown is locked rather than hidden so it's clear what's in force.
   */
  // const speakerLanguageLocked = useMemo(() => isMobileDevice(), []);

  /** Localized labels for the system-language dropdown. */
  const localizedSystemLanguageOptions = useMemo(() => {
    return enabledLanguages.map((l) => ({
      value: l.isoCode,
      label: l.languageName,
    }));
  }, [enabledLanguages]);

  /**
   * Speaker dropdown: the enabled languages.
   * The user's chosen value is the BASE iso (e.g. "ar"); a dependent dropdown
   * lets them refine to a dialect (e.g. "ar-EG") which becomes the stored value.
   */
  const speakerLanguageOptions = useMemo(() => {
    // Auto Detect commented out — the speaker language is now always picked explicitly.
    // const auto = {
    //   value: SPEAKER_AUTO,
    //   label: isArabicUi ? "اكتشاف تلقائي" : "Auto Detect",
    // };
    // if (speakerLanguageLocked) return [auto];
    // return [auto, ...enabledLanguages.map((l) => ({ value: l.isoCode, label: l.languageName }))];
    return enabledLanguages.map((l) => ({ value: l.isoCode, label: l.languageName }));
  }, [enabledLanguages]);

  const avatarLanguageOptions = useMemo(
    () => enabledLanguages.map((l) => ({ value: l.isoCode, label: l.languageName })),
    [enabledLanguages],
  );

  // The base ISO codes currently selected (drives the dialect dropdowns).
  // Auto Detect is commented out of the dropdown, so a stored "auto" (older saves, or the
  // mobile STT policy in avatar.tsx) reads as "nothing picked yet" and shows the placeholder.
  const speakerBase =
    draft.speakerLanguage === SPEAKER_AUTO ? "" : extractBaseLanguage(draft.speakerLanguage);
  const avatarBase = extractBaseLanguage(draft.avatarLanguage);

  const speakerDialects = speakerBase && speakerBase !== SPEAKER_AUTO
    ? dialectsByLang[speakerBase] ?? []
    : [];
  const avatarDialects = avatarBase ? dialectsByLang[avatarBase] ?? [] : [];

  /**
   * Turn a base iso + the dialects ace-common returned for it into the options the
   * dropdown shows. Arabic lists the four supported dialects (Lebanese, Egyptian,
   * Saudi, Bahraini), narrowed to the ones ace-common actually knows about when it
   * returned any. Every other language gets a single "Default" option whose value is
   * empty, i.e. "keep the base iso".
   */
  const buildDialectOptions = useMemo(
    () => (base: string, dialects: DialectDto[]): DialectOption[] => {
      if (!base || base === SPEAKER_AUTO) return [];
      if (base !== "ar") {
        return [{ value: "", label: isArabicUi ? "افتراضي" : "Default" }];
      }
      const known = new Set(dialects.map((d) => d.dialectName));
      const options = ARABIC_DIALECTS.filter((d) => known.size === 0 || known.has(d.iso)).map(
        (d) => ({ value: d.iso, label: isArabicUi ? d.ar : d.en }),
      );
      // ace-common only returned dialects we don't have a label for — show them raw
      // rather than an empty dropdown.
      return options.length > 0
        ? options
        : dialects.map((d) => ({ value: d.dialectName, label: d.dialectName }));
    },
    [isArabicUi],
  );

  const speakerDialectOptions = buildDialectOptions(speakerBase, speakerDialects);
  const avatarDialectOptions = buildDialectOptions(avatarBase, avatarDialects);

  // Reset draft and load ace-common data when the modal opens
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Auto Detect commented out — nothing to coerce, the stored speaker language is used as-is.
      // setDraft(
      //   speakerLanguageLocked && value.speakerLanguage !== SPEAKER_AUTO
      //     ? { ...value, speakerLanguage: SPEAKER_AUTO }
      //     : value
      // );
      setDraft(value);
      let cancelled = false;
      getEnabledLanguages()
        .then((langs) => {
          if (!cancelled) setEnabledLanguages(langs);
        })
        .catch(() => {
          if (!cancelled) setEnabledLanguages([]);
        });
      return () => {
        cancelled = true;
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, value]);

  // Lazily load dialects for the currently-selected speaker/avatar bases
  useEffect(() => {
    if (!open) return;
    const bases = new Set<string>();
    if (speakerBase && speakerBase !== SPEAKER_AUTO) bases.add(speakerBase);
    if (avatarBase) bases.add(avatarBase);

    let cancelled = false;
    bases.forEach((base) => {
      if (dialectsByLang[base] !== undefined) return;
      getDialects(base)
        .then((list) => {
          if (cancelled) return;
          setDialectsByLang((prev) => ({ ...prev, [base]: list }));
        })
        .catch(() => {
          if (cancelled) return;
          setDialectsByLang((prev) => ({ ...prev, [base]: [] }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [open, speakerBase, avatarBase, dialectsByLang]);

  // Lazily load voices for the currently-selected avatar dialect. Cache by dialect iso.
  useEffect(() => {
    if (!open) return;
    const dialect = draft.avatarLanguage;
    if (!dialect || voicesByDialect[dialect] !== undefined) return;

    let cancelled = false;
    getVoicesForDialect(dialect)
      .then((list) => {
        if (cancelled) return;
        setVoicesByDialect((prev) => ({ ...prev, [dialect]: list }));
      })
      .catch(() => {
        if (cancelled) return;
        setVoicesByDialect((prev) => ({ ...prev, [dialect]: [] }));
      });

    return () => {
      cancelled = true;
    };
  }, [open, draft.avatarLanguage, voicesByDialect]);

  // Active avatar's gender drives the voice list filter — only voices matching the
  // avatar's gender are shown. Lookup: form selection first, then the default avatar.
  const activeAvatarGender = useMemo(() => {
    const selected = avatarOptions.find(
      (a) => String(a.id) === String(draft.selectedAvatarId),
    );
    const fallback = avatarOptions.find((a) => a.isDefault) ?? avatarOptions[0];
    const a = selected ?? fallback;
    return typeof a?.gender === "number" ? a.gender : null;
  }, [avatarOptions, draft.selectedAvatarId]);

  const allDialectVoices = draft.avatarLanguage ? voicesByDialect[draft.avatarLanguage] ?? [] : [];
  const avatarVoices =
    activeAvatarGender === null
      ? allDialectVoices
      : allDialectVoices.filter((v) => v.gender === activeAvatarGender);

  // If the saved voice override no longer exists in the (gender-filtered) voice list
  // (admin deleted it, dialect changed, avatar gender flipped), clear it so we don't
  // ship a dead voiceId.
  useEffect(() => {
    if (!draft.avatarVoiceId) return;
    if (!draft.avatarLanguage) return;
    const list = voicesByDialect[draft.avatarLanguage];
    if (list === undefined) return; // still loading, don't clear yet
    const stillValid =
      activeAvatarGender === null
        ? list.some((v) => v.voiceId === draft.avatarVoiceId)
        : list.some(
            (v) => v.voiceId === draft.avatarVoiceId && v.gender === activeAvatarGender,
          );
    if (!stillValid) {
      setDraft((d) => ({ ...d, avatarVoiceId: "" }));
    }
  }, [draft.avatarVoiceId, draft.avatarLanguage, voicesByDialect, activeAvatarGender]);

  if (!open) return null;

  /**
   * Speaker base change: when the user picks a new base from the language
   * dropdown, drop any previously selected dialect (the new base may have
   * its own dialect set — or none).
   */
  const onSpeakerBaseChange = (next: string) => {
    onChange({ ...draft, speakerLanguage: next });
  };

  const onSpeakerDialectChange = (dialectIso: string) => {
    // Empty string = "no dialect, keep base"
    onChange({ ...draft, speakerLanguage: dialectIso || speakerBase });
  };

  const onAvatarBaseChange = (next: string) => {
    // Changing the base resets dialect and voice — they cascade.
    onChange({ ...draft, avatarLanguage: next, avatarVoiceId: "" });
  };

  const onAvatarDialectChange = (dialectIso: string) => {
    // Changing the dialect drops any voice override so the new dialect's default applies.
    onChange({ ...draft, avatarLanguage: dialectIso || avatarBase, avatarVoiceId: "" });
  };

  const onAvatarVoiceChange = (voiceId: string) => {
    onChange({ ...draft, avatarVoiceId: voiceId });
  };

  const onSystemLanguageChange = (next: string) => {
    // Only update the draft. The actual document RTL/dir + i18n switch happens after
    // the user clicks Save (avatar.tsx triggers a full reload when system language
    // changes, so every i18n-aware component re-mounts against the new locale).
    onChange({ ...draft, systemLanguage: next });
  };

  return (
    <div className="personalization-overlay" onClick={onClose}>
      <div
        className="personalization-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="personalization-header">
          <h2>{isArabicUi ? "التخصيص" : "Personalization"}</h2>
          <button className="close-btn" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        <div className="personalization-body">
          <div className="personalization-sidebar">
            <button
              type="button"
              className={activeTab === "general" ? "active" : ""}
              onClick={() => setActiveTab("general")}
            >
              {isArabicUi ? "عام" : "General"}
            </button>

            <button
              type="button"
              className={activeTab === "avatars" ? "active" : ""}
              onClick={() => setActiveTab("avatars")}
            >
              {isArabicUi ? "الأفاتارات" : "Avatars"}
            </button>

            <button
              type="button"
              className={activeTab === "preferences" ? "active" : ""}
              onClick={() => setActiveTab("preferences")}
            >
              {isArabicUi ? "التفضيلات" : "Preferences"}
            </button>

            <button
              type="button"
              className={activeTab === "info" ? "active" : ""}
              onClick={() => setActiveTab("info")}
            >
              {isArabicUi ? "معلومات" : "Info"}
            </button>
          </div>

          <div className="personalization-content">
            {activeTab === "general" && (
              <>
                <div className="form-group">
                  <label>{isArabicUi ? "لغة المتحدث" : "Speaker Language"}</label>
                  <select
                    value={speakerBase}
                    onChange={(e) => onSpeakerBaseChange(e.target.value)}
                  >
                    <option value="">
                      {isArabicUi ? "اختر لغة المتحدث" : "Select speaker language"}
                    </option>
                    {speakerLanguageOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {/* Auto Detect commented out, so the mobile lock hint no longer applies.
                  {speakerLanguageLocked && (
                    <small className="field-hint">
                      {isArabicUi
                        ? "الاكتشاف التلقائي مطلوب على الأجهزة المحمولة."
                        : "Auto Detect is required on mobile devices."}
                    </small>
                  )} */}
                  {speakerBase && speakerBase !== SPEAKER_AUTO && speakerDialectOptions.length > 0 && (
                    <select
                      className="dialect-select"
                      value={
                        speakerDialectOptions.some((d) => d.value === draft.speakerLanguage)
                          ? draft.speakerLanguage
                          : ""
                      }
                      onChange={(e) => onSpeakerDialectChange(e.target.value)}
                    >
                      {speakerBase === "ar" && (
                        <option value="">
                          {isArabicUi ? "بدون لهجة" : "No dialect"}
                        </option>
                      )}
                      {speakerDialectOptions.map((d) => (
                        <option key={d.value || speakerBase} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group">
                  <label>{isArabicUi ? "لغة الأفاتار" : "Avatar Language"}</label>
                  <select
                    value={avatarBase}
                    onChange={(e) => onAvatarBaseChange(e.target.value)}
                  >
                    <option value="">
                      {isArabicUi ? "اختر لغة الأفاتار" : "Select avatar language"}
                    </option>
                    {avatarLanguageOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                {avatarBase && avatarDialectOptions.length > 0 && (
                  <div className="form-group">
                    <label>{isArabicUi ? "لهجة الأفاتار" : "Avatar Dialect"}</label>
                    <select
                      value={
                        avatarDialectOptions.some((d) => d.value === draft.avatarLanguage)
                          ? draft.avatarLanguage
                          : ""
                      }
                      onChange={(e) => onAvatarDialectChange(e.target.value)}
                    >
                      {/* Arabic keeps an explicit "no dialect" entry; other languages
                          only offer "Default", which already means the plain base iso. */}
                      {avatarBase === "ar" && (
                        <option value="">
                          {isArabicUi ? "بدون لهجة" : "No dialect"}
                        </option>
                      )}
                      {avatarDialectOptions.map((d) => (
                        <option key={d.value || avatarBase} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {draft.avatarLanguage && avatarVoices.length > 0 && (
                  <div className="form-group">
                    <label>{isArabicUi ? "صوت الأفاتار" : "Avatar Voice"}</label>
                    <select
                      value={
                        avatarVoices.some((v) => v.voiceId === draft.avatarVoiceId)
                          ? draft.avatarVoiceId
                          : ""
                      }
                      onChange={(e) => onAvatarVoiceChange(e.target.value)}
                    >
                      <option value="">
                        {isArabicUi
                          ? "افتراضي (يحدده المسؤول)"
                          : "Default (selected by admin)"}
                      </option>
                      {avatarVoices.map((v) => (
                        <option key={v.id} value={v.voiceId}>
                          {v.name}
                          {v.isDefault
                            ? isArabicUi
                              ? " — افتراضي"
                              : " — default"
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>{isArabicUi ? "لغة الواجهة" : "System Language"}</label>
                  <select
                    value={draft.systemLanguage || "en"}
                    onChange={(e) => onSystemLanguageChange(e.target.value)}
                  >
                    {/* Always offer English as a fallback so guest sessions
                        (where ace-common 401s and the list is empty) still
                        have a usable option. */}
                    {localizedSystemLanguageOptions.length === 0 && (
                      <option value="en">English</option>
                    )}
                    {localizedSystemLanguageOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {activeTab === "avatars" && (
              <div className="avatar-grid">
                {avatarOptions.map((avatar) => (
                  <div
                    key={avatar.id}
                    className={`avatar-card ${
                      String(avatar.id) === draft.selectedAvatarId
                        ? "avatar-card-selected"
                        : ""
                    }`}
                    onClick={() =>
                      onChange({
                        ...draft,
                        selectedAvatarId: String(avatar.id),
                      })
                    }
                  >
                    <div className="avatar-card-header">
                      <div className="avatar-card-name">{avatar.name}</div>
                      <div className="avatar-card-header-meta">
                        <span className={`avatar-type-badge ${avatar.avatarType === "IMAGE" ? "image" : "glb"}`}>
                          {avatar.avatarType === "IMAGE" ? "Image" : "GLB"}
                        </span>
                        {String(avatar.id) === draft.selectedAvatarId && (
                          <span className="avatar-card-check">&#10003;</span>
                        )}
                      </div>
                    </div>

                    {avatar.personalityTags && avatar.personalityTags.length > 0 && (
                      <div className="avatar-card-tags">
                        {avatar.personalityTags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="avatar-tag-chip">{tag}</span>
                        ))}
                      </div>
                    )}

                    <div className="avatar-card-image-wrapper">
                      <img
                        className="avatar-card-thumbnail"
                        src={avatar.thumbnail || avatar.imagePath || ""}
                        alt={avatar.name}
                      />
                      {avatar.languageCodes && avatar.languageCodes.length > 0 && (
                        <div className="avatar-card-languages">
                          {avatar.languageCodes.map((code, idx) => (
                            <span key={idx} className="language-badge">
                              {code.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "preferences" && (
              <div className="preferences-tab">
                <div className="preference-item">
                  <div className="preference-label">
                    {isArabicUi ? "وضع التفاعل" : "Interaction Mode"}
                  </div>
                  <div className="preference-description">
                    {isArabicUi
                      ? "اختر كيفية التحدث مع الأفاتار"
                      : "Choose how you speak to the avatar"}
                  </div>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={draft.tapToTalk ? "toggle-option active" : "toggle-option"}
                      onClick={() => onChange({ ...draft, tapToTalk: true })}
                    >
                      {isArabicUi ? "اضغط للتحدث" : "Tap to Talk"}
                    </button>
                    <button
                      type="button"
                      className={!draft.tapToTalk ? "toggle-option active" : "toggle-option"}
                      onClick={() => onChange({ ...draft, tapToTalk: false })}
                    >
                      {isArabicUi ? "حر اليدين" : "Hands Free"}
                    </button>
                  </div>
                </div>

                <div className="preference-item">
                  <div className="preference-label">
                    {isArabicUi ? "عرض الدردشة" : "Chat Display"}
                  </div>
                  <div className="preference-description">
                    {isArabicUi
                      ? "اختر كيفية عرض صندوق الدردشة"
                      : "Choose how the chat box is displayed"}
                  </div>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={draft.floatingChatEnabled ? "toggle-option active" : "toggle-option"}
                      onClick={() => onChange({ ...draft, floatingChatEnabled: true })}
                    >
                      {isArabicUi ? "دردشة عائمة" : "Floating Chat"}
                    </button>
                    <button
                      type="button"
                      className={!draft.floatingChatEnabled ? "toggle-option active" : "toggle-option"}
                      onClick={() => onChange({ ...draft, floatingChatEnabled: false })}
                    >
                      {isArabicUi ? "مرئية دائمًا" : "Always Visible"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "info" && (
              <div className="info-tab">
                <p className="info-title">
                  {isArabicUi
                    ? "إليك ما يمكن لهذا المساعد القيام به:"
                    : "Here's what this assistant can do:"}
                </p>

                <ul className="info-list">
                  <li>
                    {isArabicUi
                      ? "منتج ABG Mynd 2025-6-Mynd-12M-LLM"
                      : "ABG Mynd product 2025-6-Mynd-12M-LLM"}
                  </li>
                  <li>
                    {isArabicUi
                      ? "يفهم ويولد عدة لغات"
                      : "Understands & generates multiple languages"}
                  </li>
                  <li>
                    {isArabicUi
                      ? "يدعم الصوت الفوري"
                      : "Supports real-time voice"}
                  </li>
                  <li>
                    {isArabicUi
                      ? "يتكيف مع تفضيلاتك"
                      : "Adapts to your preferences"}
                  </li>
                  <li>
                    {isArabicUi
                      ? "يتكامل مع واجهات وخدمات خارجية"
                      : "Integrates with APIs & services"}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="personalization-footer">
          <button className="save-btn" onClick={() => onSave(draft)} type="button">
            {isArabicUi ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
